// SettingsDialog.js
import React, {
    useState,
    useEffect,
    useContext,
    forwardRef,
    useRef,
    useImperativeHandle,
} from "react";
import axios from "axios";
import AutoResizedTextField from "./mdc/AutoResizedTextField";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../context/GlobalContext";

const SettingsDialog = forwardRef((props, ref) => {
    const dialogRefSettings = useRef(null);
    const dialogRefProfile = useRef(null);
    const navigate = useNavigate();
    const { auth, preferences, setPreferences, logout, viewerMode, setViewerMode } =
        useContext(GlobalContext);
    const displayLanguage = preferences.language ?? "Not Set";

    const [isOtherSelected, setIsOtherSelected] = useState(false);
    const [customInstructions, setCustomInstructions] = useState("");
    const [generalUserProfile, setGeneralUserProfile] = useState("");
    const targetLanguageOptions = ["French", "Japanese", "Korean", "German", "Other"];
    const notificationTypeOptions = [
        "Disabled",
        //"Generic",
        //"Personalized No Memory",
        "Personalized With Memory",
        //"Reminders",
    ];

    const openDialog = (dialogRef) => {
        if (dialogRef.current) {
            dialogRef.current.open = true;
        }
    };

    const closeDialog = (dialogRef) => {
        if (dialogRef.current) {
            dialogRef.current.open = false;
        }
    };

    const handlePageChange = (page) => {
        if (page === "settings") {
            openDialog(dialogRefSettings);
            closeDialog(dialogRefProfile);
        } else if (page === "profile") {
            const isAuthed = Boolean(auth?.isAuthenticated);
            const hasToken = Boolean(auth?.accessToken);
            // console.debug("[SETTINGS_PROFILE] gate check", { isAuthed, hasToken });
            if (isAuthed && hasToken) {
                fetchPersonalizationProfile();
            } else {
                // console.warn("[SETTINGS_PROFILE] blocked: auth not ready");
            }
            openDialog(dialogRefProfile);
            closeDialog(dialogRefSettings);
        }
    };

    const openSettingsDefault = () => {
        handlePageChange("settings");
    };

    useImperativeHandle(ref, () => ({
        openSettingsDefault,
        openDialog,
    }));

    const handleLanguageChange = async (newLanguage) => {
        setPreferences((prevPreferences) => ({
            ...prevPreferences,
            language: newLanguage,
        }));
        await updatePreferences({ language: newLanguage });

        // List of languages that use non-Roman scripts
        const nonRomanLanguages = [
            "Japanese",
            "Korean",
            "Armenian",
            "Russian",
            "Persian",
            "Traditional Chinese",
            "Simplified Chinese",
            "Mandarin",
            "Arabic",
        ];

        // Check if the selected language is in the list of non-Roman languages
        if (nonRomanLanguages.includes(newLanguage)) {
            await updatePreferences({ showTransliteration: true });
        }
    };

    const updatePreferences = async (newPreferences) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/update-preferences`,
                newPreferences,
                {
                    withCredentials: true,
                }
            );
            await fetchPreferences(); // Fetch preferences to update UI
        } catch (error) {
            console.error("Error updating preferences:", error);
        }
    };

    const fetchPreferences = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/user/get-preferences`
            );
            const prefs = await response.data;
            if (prefs.message === "User authentication required") {
                console.error(
                    prefs.message,
                    "User authentication required for fetchPreferences"
                );
            } else {
                setPreferences({
                    baseLanguage: prefs.baseLanguage,
                    language: prefs.language,
                    showTransliteration: prefs.showTransliteration,
                    theme: prefs.theme,
                    masteryThreshold: prefs.masteryThreshold,
                    promptNotificationsPerWeek: prefs.promptNotificationsPerWeek,
                    notificationType: prefs.notificationType,
                });
            }
        } catch (error) {
            console.error("Error fetching preferences:", error);
        }
    };

    const fetchPersonalizationProfile = async () => {
        if (!auth?.isAuthenticated || !auth?.accessToken) {
/*             console.warn(
                "[SETTINGS_INIT] fetchPersonalizationProfile blocked: auth not ready"
            ); */
            return;
        }
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/user/get-personalization-profile`
            );
            const profile = await response.data;
            if (!profile || profile.message === "User authentication required") {
                console.error(
                    profile?.message,
                    "User authentication required for fetchPersonalizationProfile"
                );
            } else {
                setGeneralUserProfile(profile.generalUserProfile || "");
                setCustomInstructions(profile.customInstructions || "");
            }
        } catch (error) {
            console.error("Error fetching personalization profile:", error);
        }
    };

    const updatePersonalizationProfile = async (newProfile) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/update-personalization-profile`,
                newProfile,
                { withCredentials: true }
            );
            await fetchPersonalizationProfile();
        } catch (error) {
            console.error("Error updating personalization profile:", error);
        }
    };

    useEffect(() => {
        const isAuthed = Boolean(auth?.isAuthenticated);
        const hasToken = Boolean(auth?.accessToken);
        //console.debug("[SETTINGS_INIT] gate check", { isAuthed, hasToken });

        if (!isAuthed || !hasToken) {
            //console.warn("[SETTINGS_INIT] blocked: auth not ready");
            return;
        }

        //console.debug("[SETTINGS_INIT] fetching");
        fetchPreferences();
        fetchPersonalizationProfile();
        setIsOtherSelected(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.isAuthenticated, auth?.accessToken]);

    const selectLanguage = (event) => {
        const selectedLanguage = event.target.value;
        // Return early if other is selected
        if (selectedLanguage === "Other") {
            setIsOtherSelected(true);
            return;
        }
        setIsOtherSelected(false);
        handleLanguageChange(selectedLanguage);
    };

    const handleOtherLanguageChange = (event) => {
        const otherLanguage = event.target.value;
        handleLanguageChange(otherLanguage);
    };

    const handleMasteryThresholdChange = (event) => {
        const selectedMasteryThreshold = event.target.value;
        if (!selectedMasteryThreshold) return;
        updatePreferences({ masteryThreshold: selectedMasteryThreshold });
    };

    const handlePromptFrequencyChange = (event) => {
        const value = Number(event.target.value);
        updatePreferences({ promptNotificationsPerWeek: value });
    };

    const handleNotificationTypeChange = (event) => {
        const value = event.target.value;
        if (value === "Personalized With Memory") {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            updatePreferences({ notificationType: value, timezone: tz });
        } else {
            updatePreferences({ notificationType: value });
        }
    };

    const handleTransliterationVisibilityChange = (event) => {
        const visibility = event.target.selected;
        updatePreferences({ showTransliteration: visibility });
    };

    const handleThemeChange = (event) => {
        const isSelected = event.target.selected;
        const selectedTheme = !isSelected ? "light" : "dark";
        updatePreferences({ theme: selectedTheme });
    };
    const handleLogout = (e) => {
        logout();
        navigate("/login");
    };
    const handleSubmit = (e) => {
        dialogRefSettings.current.open = false;
    };
    const closeSettingsDialog = (e) => {
        dialogRefSettings.current.open = false;
    };
    const closeProfileDialog = (e) => {
        dialogRefProfile.current.open = false;
    };
    const handleProfileSave = async () => {
        await updatePersonalizationProfile({
            customInstructions,
            generalUserProfile: generalUserProfile,
        });
        closeProfileDialog();
    };
    return (
        <div className="settings-dialog">
            <md-dialog ref={dialogRefSettings}>
                <span slot="headline" className="headline">
                    Settings
                    <md-icon-button
                        className="dialog-close-button"
                        value="close"
                        aria-label="Close settings"
                        onClick={closeSettingsDialog}
                    >
                        <md-icon>close</md-icon>
                    </md-icon-button>
                </span>
                <form
                    className="settings-form"
                    slot="content"
                    method="dialog"
                    onSubmit={handleSubmit}
                >
                    <div>
                        <md-filled-select
                            required
                            label="Target Language"
                            onInput={selectLanguage}
                            value={displayLanguage}
                        >
                            <md-select-option
                                selected
                                value={displayLanguage}
                                aria-label={displayLanguage}
                            >
                                <div slot="headline">{displayLanguage}</div>
                            </md-select-option>
                            {targetLanguageOptions
                                .filter((lang) => lang !== preferences.language)
                                .map((lang) => (
                                    <md-select-option key={lang} value={lang}>
                                        <div slot="headline">{lang}</div>
                                    </md-select-option>
                                ))}
                        </md-filled-select>
                    </div>
                    {isOtherSelected && (
                        <div>
                            <md-filled-text-field
                                label="Enter Other Language"
                                id="otherLanguageInput"
                                placeholder="Enter language"
                                value={preferences.language}
                                onBlur={handleOtherLanguageChange}
                                required
                            />
                        </div>
                    )}
                    <div>
                        <md-filled-text-field
                            label="Mastery Threshold"
                            supporting-text="Times a word must be seen to be mastered"
                            editable
                            type="number"
                            value={preferences.masteryThreshold}
                            required
                            onInput={handleMasteryThresholdChange}
                            min="0"
                            max="50"
                        />
                    </div>
                    <div>
                        <md-filled-select
                            label="Notification Type"
                            onInput={handleNotificationTypeChange}
                            value={preferences.notificationType}
                        >
                            <md-select-option
                                selected
                                value={preferences.notificationType}
                                aria-label={preferences.notificationType}
                            >
                                <div slot="headline">{preferences.notificationType}</div>
                            </md-select-option>
                            {notificationTypeOptions
                                .filter((nt) => nt !== preferences.notificationType) // ⬅️ skip selected value
                                .map((nt) => (
                                    <md-select-option key={nt} value={nt}>
                                        <div slot="headline">{nt}</div>
                                    </md-select-option>
                                ))}
                        </md-filled-select>
                    </div>
                    {preferences.notificationType !== "Disabled" && (
                        <div>
                            <md-filled-text-field
                                label="Prompt Notifications per Week"
                                type="number"
                                value={preferences.promptNotificationsPerWeek}
                                onInput={handlePromptFrequencyChange}
                                min="1"
                                max="14"
                            />
                        </div>
                    )}
                    <div>
                        <label className="settings-toggle">
                            Pronunciation
                            <md-switch
                                selected={preferences.showTransliteration ? true : undefined}
                                onInput={handleTransliterationVisibilityChange}
                            ></md-switch>
                        </label>
                    </div>
                    <div>
                        <label className="settings-toggle">
                            <span>Dark Theme</span>
                            <md-switch
                                selected={preferences.theme === "dark" ? true : undefined}
                                onInput={handleThemeChange}
                            ></md-switch>
                        </label>
                    </div>
                    {auth.userType === "admin" && (
                        <div>
                            <label className="settings-toggle">
                                <span>Viewer Mode</span>
                                <md-switch
                                    selected={viewerMode ? true : undefined}
                                    onInput={() => setViewerMode(!viewerMode)}
                                ></md-switch>
                            </label>
                        </div>
                    )}{" "}
                    <md-outlined-button onClick={handleLogout}>Log Out</md-outlined-button>
                    <md-outlined-button onClick={() => navigate("/resetPassword")}>
                        Reset Password
                    </md-outlined-button>
                    <md-filled-button onClick={() => handlePageChange("profile")}>
                        Personalization
                    </md-filled-button>
                </form>
            </md-dialog>
            <md-dialog ref={dialogRefProfile}>
                <span slot="headline" className="headline">
                    Personalization Profile
                    <md-icon-button
                        className="dialog-close-button"
                        value="close"
                        aria-label="Close personalization"
                        onClick={closeProfileDialog}
                    >
                        <md-icon>close</md-icon>
                    </md-icon-button>
                </span>
                <form className="settings-form" slot="content" method="dialog">
                    <div>
                        <AutoResizedTextField
                            type="textarea"
                            minRows={3}
                            label="General Profile"
                            placeholder="e.g. I really like Japanese music, and want to travel to Japan one day!"
                            value={generalUserProfile}
                            onInput={(e) => setGeneralUserProfile(e.target.value)}
                            editable
                        />
                    </div>
                    <div>
                        <AutoResizedTextField
                            type="textarea"
                            minRows={3}
                            label="Custom Instructions"
                            placeholder="e.g. Ask me about my language learning progress on the weekends; Avoid shallow conversations."
                            value={customInstructions}
                            onInput={(e) => setCustomInstructions(e.target.value)}
                            editable
                        />
                    </div>
                </form>
                <div slot="actions">
                    <md-text-button onClick={() => handlePageChange("settings")}>
                        Back
                    </md-text-button>
                    <md-filled-button onClick={handleProfileSave}>Save</md-filled-button>
                </div>
            </md-dialog>
        </div>
    );
});

export default SettingsDialog;
